import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Search, Send, Eye, Mail, CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("sl-SI", { dateStyle: "short", timeStyle: "short" }) : "";

/**
 * Super-admin tool for sending bulk invitation emails to LD presidents.
 * - Lists eligible LDs (inactive, has email)
 * - Preview email before send
 * - Batch send up to 100 at a time
 * - History log
 */
export const SuperAdminInvitations = () => {
  const { token } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [customMessage, setCustomMessage] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(null); // { current, total, sent, failed, skipped }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [history, setHistory] = useState({ entries: [], total: 0 });
  const [showHistory, setShowHistory] = useState(false);
  const [template, setTemplate] = useState("warm"); // "warm" | "practical"
  const [abStats, setAbStats] = useState(null);

  const loadAbStats = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/invitations/ab-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAbStats(res.data);
    } catch (_) { /* silent */ }
  };

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "2000" });
      if (search.trim()) params.set("search", search.trim());
      const res = await axios.get(`${API}/superadmin/invitations/candidates?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCandidates(res.data.candidates || []);
      setTotal(res.data.total || 0);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri branju kandidatov");
    } finally { setLoading(false); }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/invitations/history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadCandidates(); loadHistory(); }, []); // eslint-disable-line

  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map(c => c.id)));
  };
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  // Hitri gumbi za skupinsko izbiro po 100 (npr. "Izberi 1-100", "Izberi 101-200"…)
  const BATCH_SIZE = 100;
  const groupCount = Math.ceil(candidates.length / BATCH_SIZE);
  const selectGroup = (groupIdx) => {
    const start = groupIdx * BATCH_SIZE;
    const end = Math.min(start + BATCH_SIZE, candidates.length);
    const ids = candidates.slice(start, end).map(c => c.id);
    setSelected(new Set(ids));
  };
  const addGroupToSelection = (groupIdx) => {
    const start = groupIdx * BATCH_SIZE;
    const end = Math.min(start + BATCH_SIZE, candidates.length);
    const next = new Set(selected);
    candidates.slice(start, end).forEach(c => next.add(c.id));
    setSelected(next);
  };

  const openPreview = async (ldId) => {
    try {
      const params = new URLSearchParams({ template });
      if (ldId) params.set("ld_id", ldId);
      const res = await axios.get(`${API}/superadmin/invitations/preview?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreviewSubject(res.data.subject);
      setPreviewHtml(res.data.html);
    } catch (e) {
      toast.error("Napaka pri predogledu");
    }
  };

  const send = async () => {
    if (selected.size === 0) { toast.error("Ni izbranih LD-jev"); return; }
    setSending(true);
    const allIds = Array.from(selected);
    // Razbij v svežnje po BATCH_SIZE (Brevo throttle safe)
    const chunks = [];
    for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
      chunks.push(allIds.slice(i, i + BATCH_SIZE));
    }
    const totals = { sent: 0, failed: 0, skipped: 0 };
    setSendProgress({ current: 0, total: chunks.length, ...totals });
    try {
      for (let idx = 0; idx < chunks.length; idx++) {
        const chunk = chunks[idx];
        try {
          const res = await axios.post(`${API}/superadmin/invitations/send`, {
            ld_ids: chunk,
            custom_message: customMessage.trim() || null,
            template,
          }, { headers: { Authorization: `Bearer ${token}` } });
          totals.sent += res.data.sent_count || 0;
          totals.failed += res.data.failed_count || 0;
          totals.skipped += res.data.skipped_count || 0;
        } catch (e) {
          // Če sveženj v celoti pade, štej vse kot napake in nadaljuj
          totals.failed += chunk.length;
          toast.error(`Sveženj ${idx + 1}/${chunks.length}: ${e.response?.data?.detail || "napaka"}`);
        }
        setSendProgress({ current: idx + 1, total: chunks.length, ...totals });
        // Kratka pavza med svežnji, da ne obremenimo Brevo
        if (idx < chunks.length - 1) {
          await new Promise(r => setTimeout(r, 1500));
        }
      }
      toast.success(`Zaključeno · Poslano: ${totals.sent} · Napake: ${totals.failed} · Preskočeno: ${totals.skipped}`);
      setConfirmOpen(false);
      setSelected(new Set());
      setCustomMessage("");
      await loadCandidates();
      await loadHistory();
    } finally {
      setSending(false);
      setSendProgress(null);
    }
  };

  const filteredCount = candidates.length;
  const selectedCount = selected.size;
  const alreadyInvited = useMemo(() => candidates.filter(c => c.last_invited_at).length, [candidates]);

  return (
    <div className="space-y-4" data-testid="sa-invitations">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-primary">E-mail vabila predsednikom LD</h2>
          <p className="text-xs text-muted-foreground">
            <b>{total}</b> kandidatov (neaktivne LD z e-mailom) · <b>{alreadyInvited}</b> že povabljenih · <b>{selectedCount}</b> izbranih
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { setShowHistory(!showHistory); if (!showHistory) { loadHistory(); loadAbStats(); } }} data-testid="sa-inv-history-toggle">
            <Clock className="h-4 w-4 mr-1" />{showHistory ? "Skrij" : "Zgodovina"} ({history.total})
          </Button>
          <Button variant="outline" size="sm" onClick={() => openPreview()} data-testid="sa-inv-preview-sample">
            <Eye className="h-4 w-4 mr-1" />Predogled e-maila
          </Button>
          <Button variant="outline" size="sm" onClick={loadCandidates}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {showHistory ? (
        <>
          {/* A/B stats */}
          {abStats && (abStats.warm.sent > 0 || abStats.practical.sent > 0) && (
            <Card data-testid="sa-inv-ab-stats">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">A/B primerjava konverzije</CardTitle>
                <CardDescription className="text-xs">Delež poslanih vabil, ki so aktivirali brezplačen preizkus.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: "warm", label: "🦌 Topla & osebna", data: abStats.warm },
                    { key: "practical", label: "⚡ Kratka & praktična", data: abStats.practical },
                  ].map(({ key, label, data }) => (
                    <div key={key} className="p-3 border rounded-lg space-y-2" data-testid={`ab-stat-${key}`}>
                      <p className="text-xs font-medium">{label}</p>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <p className="text-xl font-bold text-blue-700" data-testid={`ab-open-${key}`}>{data.open_rate_pct}%</p>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Odprtost</p>
                        </div>
                        <div>
                          <p className="text-xl font-semibold text-amber-700" data-testid={`ab-ctr-${key}`}>{data.click_rate_pct}%</p>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wide">CTR</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-primary" data-testid={`ab-conv-${key}`}>{data.conversion_pct}%</p>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Konverzija</p>
                        </div>
                      </div>
                      <div className="text-[10px] text-muted-foreground border-t pt-1.5">
                        <span>Poslano: <b>{data.sent}</b></span>
                        <span className="mx-1">·</span>
                        <span>Odprto: <b>{data.opened_invs}</b></span>
                        <span className="mx-1">·</span>
                        <span>Klikov: <b>{data.clicks}</b></span>
                        <span className="mx-1">·</span>
                        <span>Aktivnih: <b>{data.activated}</b></span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Zgodovina poslanih vabil</CardTitle>
            <CardDescription className="text-xs">Zadnjih {history.entries.length} od {history.total} vabil.</CardDescription>
          </CardHeader>
          <CardContent>
            {history.entries.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Ni poslanih vabil.</p>
            ) : (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {history.entries.map(h => (
                  <div key={h.id} className="p-2 border rounded text-xs flex items-center gap-2" data-testid={`inv-history-${h.id}`}>
                    {h.status === "sent"
                      ? <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                      : <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                    }
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{h.ld_naziv}</div>
                      <div className="text-muted-foreground">{h.email}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {h.template === "practical" ? "⚡ Kratka" : "🦌 Topla"}
                    </Badge>
                    <div className="text-muted-foreground text-[10px] text-right flex-shrink-0">{fmtDate(h.sent_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>
      ) : (
        <>
          {/* Template A/B selector + Search + optional message */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 p-3 bg-primary/5 border border-primary/15 rounded-lg" data-testid="sa-inv-template-selector">
              <span className="text-sm font-medium text-primary">Predloga:</span>
              <Button
                size="sm"
                variant={template === "warm" ? "default" : "outline"}
                onClick={() => setTemplate("warm")}
                data-testid="tpl-warm"
              >🦌 Topla & osebna</Button>
              <Button
                size="sm"
                variant={template === "practical" ? "default" : "outline"}
                onClick={() => setTemplate("practical")}
                data-testid="tpl-practical"
              >⚡ Kratka & praktična</Button>
              <span className="ml-auto text-[11px] text-muted-foreground">A/B test – izmenjujte za merjenje odziva</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Išči po nazivu, kodi ali e-mailu…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadCandidates(); }}
                className="pl-9"
                data-testid="sa-inv-search"
              />
            </div>

            {/* Hitra izbira po skupinah po 100 – rešitev za Brevo throttle */}
            {groupCount > 1 && (
              <div className="flex flex-wrap items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg" data-testid="sa-inv-batch-selector">
                <span className="text-xs font-medium text-amber-900 dark:text-amber-200">
                  Hitra izbira ({candidates.length} LD v {groupCount} skupinah po 100):
                </span>
                {Array.from({ length: groupCount }).map((_, i) => {
                  const start = i * BATCH_SIZE + 1;
                  const end = Math.min((i + 1) * BATCH_SIZE, candidates.length);
                  return (
                    <div key={i} className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => selectGroup(i)}
                        className="h-7 text-[11px]"
                        data-testid={`sa-inv-select-group-${i}`}
                        title={`Označi samo ${start}.–${end}. LD`}
                      >
                        {start}–{end}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => addGroupToSelection(i)}
                        className="h-7 px-1.5 text-[11px]"
                        data-testid={`sa-inv-add-group-${i}`}
                        title={`Dodaj ${start}.–${end}. k obstoječi izbiri`}
                      >+</Button>
                    </div>
                  );
                })}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelected(new Set(candidates.map(c => c.id)))}
                  className="h-7 text-[11px]"
                  data-testid="sa-inv-select-all-visible"
                >
                  Izberi vse ({candidates.length})
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected(new Set())}
                  className="h-7 text-[11px]"
                  data-testid="sa-inv-clear-selection"
                >
                  Počisti
                </Button>
                <span className="ml-auto text-[10px] text-muted-foreground">
                  Vabila se pošljejo v svežnjih po 100 (~1,5s pavza med njimi).
                </span>
              </div>
            )}
            <details className="border rounded-lg p-2">
              <summary className="cursor-pointer text-sm font-medium text-primary">
                Dodatno osebno sporočilo (neobvezno)
              </summary>
              <Textarea
                placeholder="Če želite, vpišite dodatek k standardnemu vabilu (npr. 'Pišem vam po sestanku LZS 5.2.2026…')"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="mt-2"
                rows={3}
                maxLength={500}
                data-testid="sa-inv-custom-msg"
              />
              <p className="text-[10px] text-muted-foreground mt-1">{customMessage.length}/500</p>
            </details>
          </div>

          {/* Candidates table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
              ) : candidates.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">Ni kandidatov.</p>
              ) : (
                <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/50 backdrop-blur">
                      <tr className="text-left border-b">
                        <th className="p-2">
                          <Checkbox
                            checked={selected.size === candidates.length && candidates.length > 0}
                            onCheckedChange={toggleAll}
                            data-testid="sa-inv-toggle-all"
                          />
                        </th>
                        <th className="p-2 font-medium">LD</th>
                        <th className="p-2 font-medium hidden md:table-cell">E-mail</th>
                        <th className="p-2 font-medium hidden lg:table-cell">Nazadnje povabljen</th>
                        <th className="p-2 font-medium text-right">Akcije</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map(c => (
                        <tr key={c.id} className="border-b hover:bg-muted/20" data-testid={`inv-row-${c.id}`}>
                          <td className="p-2">
                            <Checkbox
                              checked={selected.has(c.id)}
                              onCheckedChange={() => toggle(c.id)}
                              data-testid={`inv-select-${c.id}`}
                            />
                          </td>
                          <td className="p-2">
                            <div className="font-medium">{c.naziv}</div>
                            <div className="text-[11px] text-muted-foreground">{c.koda}</div>
                          </td>
                          <td className="p-2 text-xs hidden md:table-cell">{c.kontakt_email}</td>
                          <td className="p-2 text-xs hidden lg:table-cell">
                            {c.last_invited_at ? (
                              <Badge variant="secondary" className="text-[10px]">{fmtDate(c.last_invited_at)}</Badge>
                            ) : (
                              <span className="text-muted-foreground">–</span>
                            )}
                          </td>
                          <td className="p-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => openPreview(c.id)} data-testid={`inv-preview-${c.id}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="sticky bottom-4 z-10 flex justify-end gap-2">
            <Button
              size="lg"
              disabled={selectedCount === 0 || sending}
              onClick={() => setConfirmOpen(true)}
              className="shadow-lg"
              data-testid="sa-inv-send-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Pošlji vabila ({selectedCount}
              {selectedCount > 100 ? ` – ${Math.ceil(selectedCount / 100)} svežnjev` : ""})
            </Button>
          </div>
        </>
      )}

      {/* Preview dialog */}
      <Dialog open={!!previewHtml} onOpenChange={(o) => { if (!o) setPreviewHtml(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="sa-inv-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5" />Predogled e-maila</DialogTitle>
            <DialogDescription className="text-xs">
              Zadeva: <b>{previewSubject}</b>
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto rounded border bg-white">
            <div dangerouslySetInnerHTML={{ __html: previewHtml || "" }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewHtml(null)}>Zapri</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!sending) setConfirmOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="sa-inv-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Potrditev pošiljanja</DialogTitle>
            <DialogDescription>
              Ali res želite poslati <b>{selectedCount}</b> vabil?
              {selectedCount > 100 && (
                <> Selekcija bo razbita v <b>{Math.ceil(selectedCount / 100)}</b> svežnjev po največ 100 (~1,5s pavza med njimi zaradi Brevo throttle).</>
              )}
              {" "}Dejanje ni razveljavljivo.
            </DialogDescription>
          </DialogHeader>
          {customMessage.trim() && (
            <div className="text-xs bg-amber-50 border border-amber-200 p-2 rounded">
              <b>Osebno sporočilo bo vključeno:</b>
              <p className="mt-1 whitespace-pre-wrap">{customMessage}</p>
            </div>
          )}
          {sendProgress && (
            <div className="space-y-2" data-testid="sa-inv-send-progress">
              <div className="flex justify-between text-xs">
                <span>Sveženj <b>{sendProgress.current}</b> / {sendProgress.total}</span>
                <span>Poslano: <b className="text-green-700">{sendProgress.sent}</b> · Napake: <b className="text-red-700">{sendProgress.failed}</b> · Preskočeno: <b>{sendProgress.skipped}</b></span>
              </div>
              <div className="w-full h-2 bg-muted rounded overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${(sendProgress.current / sendProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>Prekliči</Button>
            <Button onClick={send} disabled={sending} data-testid="sa-inv-confirm-send">
              {sending
                ? (sendProgress ? `Pošiljam sveženj ${sendProgress.current}/${sendProgress.total}…` : "Pošiljam…")
                : `Pošlji ${selectedCount} vabil`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
